import { Edit, Star } from "lucide-react";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Textarea } from "../../components/ui/textarea";

const StarRating = ({
  rating,
  hoveredRating,
  setHoveredRating,
  setRating,
  size = "w-8 h-8",
  disabled = false,
}) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`${size} ${
          disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
        } transition-colors ${
          star <= (hoveredRating || rating)
            ? "fill-yellow-400 text-yellow-400"
            : "text-gray-300"
        }`}
        values={rating}
        onMouseEnter={() => !disabled && setHoveredRating(star)}
        onMouseLeave={() => !disabled && setHoveredRating(0)}
        onClick={() => !disabled && setRating(star)}
      />
    ))}
  </div>
);

const ComprehensiveRating = ({
  orderItems,
  onSubmit,
  rating,
  feedback,
  menuRating,
  orderingMethod,
}) => {
  const { t } = useTranslation();

  // Use state to track if there's existing data
  const [hasExistingData, setHasExistingData] = useState(rating > 0);
  const [editMode, setEditMode] = useState(!hasExistingData);
  const [overallRating, setOverallRating] = useState(rating || 0);
  const [overallHoveredRating, setOverallHoveredRating] = useState(0);
  const [overallFeedback, setOverallFeedback] = useState(feedback || "");

  // Initialize item ratings with stored values, or default to 0 if not found
  const [itemRatings, setItemRatings] = useState(
    orderItems.map((item) => {
      const existingRating = menuRating?.find((r) => r.id === item.id)?.rating;
      return {
        id: item.id,
        name: item.name,
        rating: existingRating !== undefined ? existingRating : 0, // Use stored value or default to 0
      };
    })
  );

  const [itemHoveredRatings, setItemHoveredRatings] = useState({});

  // Update hasExistingData when the rating changes
  useEffect(() => {
    setHasExistingData(rating > 0);
  }, [rating]);

  const handleItemRating = (itemId, rating) => {
    setItemRatings((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, rating } : item))
    );
  };

  const handleSubmit = () => {
    onSubmit({
      overallRatings: { rating: overallRating },
      itemRatings,
      overallFeedback,
    });

    // Set hasExistingData to true after submission
    setHasExistingData(true);
    setEditMode(false);
  };

  const toggleEditMode = () => {
    setEditMode(!editMode);
  };

  const isSubmitDisabled = overallRating === 0;

  return (
    <Card className="mt-6 relative">
      <CardHeader className="flex flex-row items-center justify-between pr-6">
        <CardTitle>{t("rating.title")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overall Rating */}
        <div className="space-y-4">
          <Label className="text-lg font-semibold">
            {t("rating.overall_experience")}
          </Label>
          <div className="space-y-2">
            <Label className="text-base">{t("rating.how_was_order")}</Label>
            <StarRating
              rating={overallRating}
              hoveredRating={overallHoveredRating}
              setHoveredRating={setOverallHoveredRating}
              setRating={setOverallRating}
              disabled={!editMode}
            />
          </div>
        </div>

        <Separator />

        {/* Individual Item Ratings */}
        {orderingMethod !== "table_reservation" && (
          <div className="space-y-6">
            <div className="space-y-4">
              <Label className="text-lg font-semibold">
                {t("rating.ordered_dished")}
              </Label>
              {itemRatings.map((item) => (
                <div key={item.id} className="space-y-2">
                  <Label className="text-base">{item.name}</Label>
                  <StarRating
                    rating={item.rating}
                    hoveredRating={itemHoveredRatings[item.id] || 0}
                    setHoveredRating={(rating) =>
                      setItemHoveredRatings((prev) => ({
                        ...prev,
                        [item.id]: rating,
                      }))
                    }
                    setRating={(rating) => handleItemRating(item.id, rating)}
                    size="w-6 h-6"
                    disabled={!editMode}
                  />
                </div>
              ))}
            </div>

            <Separator />
          </div>
        )}

        {/* Overall Feedback */}
        <div className="space-y-2">
          <Label className="text-lg font-semibold">
            {t("rating.add_detailed_review")}
          </Label>
          <Textarea
            placeholder={t("rating.add_detailed_review_placeholder")}
            value={overallFeedback}
            onChange={(e) => setOverallFeedback(e.target.value)}
            className="min-h-32"
            maxLength={200}
            disabled={!editMode}
          />
        </div>
        {hasExistingData && (
          <Button onClick={toggleEditMode} className="flex items-center gap-1">
            <Edit className="w-4 h-4" />
            <span>{editMode ? "Cancel" : "Edit"}</span>
          </Button>
        )}
        {/* Submit Button - Only show if in edit mode */}
        {editMode && (
          <Button
            onClick={handleSubmit}
            className="w-full"
            disabled={isSubmitDisabled}
          >
            {t("rating.submit")}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default ComprehensiveRating;
